import { Button } from '@/components/ui/button';
import { captureError } from '@/lib/analytics';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

// Erro real reportado num app irmão (prestador.tamoaki.co.ao, 2026-09-11): um
// separador aberto desde antes do último deploy tenta carregar um chunk .js
// lazy com o hash do build antigo — já apagado do servidor. O rewrite de SPA
// (vercel.json) devolve index.html, e o browser recusa executá-lo como JS.
// "Tentar Novamente" sozinho não resolve aqui: o wrapper lazy() falhado fica
// em cache (utils/lazyLoading.tsx), por isso um simples re-render volta a
// lançar o mesmo erro — só um reload real da página busca os chunks certos.
const PADRAO_CHUNK_DESATUALIZADO =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/i;

const FLAG_RECARREGADO = 'tamoaki_chunk_reload_attempted';

export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  // Centraliza a deteção de chunk desatualizado — usada no auto-reload, no
  // retry manual e na mensagem mostrada, para nunca desalinhar os 3 sítios.
  private isChunkError(error: Error | null): boolean {
    return !!error && PADRAO_CHUNK_DESATUALIZADO.test(error.message);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Capturar erro no PostHog Error Tracking (substitui o Sentry).
    captureError(error, {
      component: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
    });

    this.setState({ error, errorInfo });

    // Chamar callback personalizado se fornecido
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log para desenvolvimento
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Chunk desatualizado: recarregar sozinho, uma única vez por sessão, em
    // vez de deixar o utilizador preso num "Tentar Novamente" que nunca
    // vai funcionar sem um reload real. sessionStorage pode lançar exceção
    // em navegação privada/armazenamento bloqueado — nunca deixar isso
    // partir o próprio tratamento de erro.
    if (this.isChunkError(error)) {
      try {
        if (sessionStorage.getItem(FLAG_RECARREGADO)) return;
        sessionStorage.setItem(FLAG_RECARREGADO, '1');
      } catch {
        // Sem sessionStorage disponível — seguir em frente e recarregar na
        // mesma; pior caso é um reload extra, não um crash do boundary.
      }
      window.location.reload();
    }
  }

  handleRetry = () => {
    if (this.isChunkError(this.state.error)) {
      // Um simples re-render relança o mesmo erro (o lazy() falhado fica em
      // cache) — só um reload real busca os chunks atuais do servidor.
      window.location.reload();
      return;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Renderizar fallback personalizado se fornecido
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Renderizar UI de erro padrão
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            
            <div className="text-center">
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {this.isChunkError(this.state.error) ? 'Esta página foi atualizada' : 'Ocorreu um erro'}
              </h1>

              <p className="text-gray-600 mb-6">
                {this.isChunkError(this.state.error)
                  ? 'Recarregue para continuar a usar a versão mais recente.'
                  : 'Encontrámos um erro inesperado. A nossa equipa foi notificada e está a trabalhar para o corrigir.'}
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="mb-4 p-3 bg-gray-100 rounded text-left">
                  <p className="text-sm font-mono text-red-600">
                    {this.state.error.message}
                  </p>
                  {this.state.errorInfo && (
                    <pre className="text-xs text-gray-500 mt-2 overflow-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}

              {this.state.eventId && (
                <p className="text-sm text-gray-500 mb-4">
                  ID do erro: {this.state.eventId}
                </p>
              )}

              <div className="flex gap-3 justify-center">
                <Button
                  onClick={this.handleRetry}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar novamente
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Ir para o início
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SentryErrorBoundary;